import { Dialog, Transition } from '@headlessui/react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import {
  Badge,
  timestampFormatter,
  underscoreFormatter,
  warrantyStateColor,
} from '@retainerclub/common';
import PropTypes from 'prop-types';
import { Fragment } from 'react';

// Humanizes the raw Warranty state enum. The previous local mapping only
// covered pending/approved/rejected plus three states that do not exist in
// Warranties::StateMachine, so every review state rendered with its underscores
// showing.
const mapWarrantyState = (value) => {
  if (!value) {
    return '—';
  }

  return underscoreFormatter(value);
};

function WarrantyListModal({
  isOpen,
  onClose,
  warranties = [],
  onSelectWarranty,
  title = 'Perfect Fit Guarantees',
}) {
  const sortedWarranties = [...warranties].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  function handleSelect(id) {
    onSelectWarranty(id);
    onClose();
  }

  return (
    <Transition.Root as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-xl bg-white shadow-xl transition-all sm:max-w-md">
                <div className="flex items-center justify-between border-gray-200 border-b px-5 py-4">
                  <Dialog.Title
                    as="h2"
                    className="font-semibold text-gray-900 text-lg"
                  >
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-500 hover:bg-rcprimary-50 hover:text-rcprimary-600 focus:outline-none focus:ring-2 focus:ring-rcprimary-500"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <XMarkIcon aria-hidden className="size-5" />
                  </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
                  {sortedWarranties.length === 0 ? (
                    <p className="text-center text-gray-500 text-sm">
                      No PFGs found.
                    </p>
                  ) : (
                    <>
                      <p className="mb-3 text-gray-500 text-sm">
                        Select a PFG to view details
                      </p>
                      <ul className="space-y-2">
                        {sortedWarranties.map((w) => (
                          <li key={w.id}>
                            <button
                              type="button"
                              onClick={() => handleSelect(w.id)}
                              className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-rcprimary-500 focus:ring-offset-2"
                            >
                              <div className="min-w-0 flex-1">
                                <span className="font-semibold text-gray-900 text-sm">
                                  Case {w.hexCode?.toUpperCase() ?? '—'}
                                </span>
                                <Badge
                                  className="ml-2"
                                  color={warrantyStateColor(w.state) || 'gray'}
                                  name={mapWarrantyState(w.state)}
                                  size="small"
                                  withDot={false}
                                />
                                <p className="mt-0.5 text-gray-500 text-xs">
                                  Submitted {timestampFormatter(w.createdAt)}
                                </p>
                              </div>
                              <ChevronRightIcon
                                aria-hidden
                                className="size-5 shrink-0 text-gray-400"
                              />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                <div className="border-gray-200 border-t px-5 py-4">
                  <button
                    type="button"
                    className="w-full rounded-md border border-transparent bg-green-600 px-4 py-2 font-medium text-sm text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

WarrantyListModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  warranties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      hexCode: PropTypes.string,
      state: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  ),
  onSelectWarranty: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default WarrantyListModal;
